export type SchedulerReviewStatus = 'feasible_with_gaps' | 'feasible' | 'no_feasible_schedule';
export type SchedulerReviewConflictSeverity = 'critical' | 'gap' | 'warning' | 'review';
export type SchedulerReviewResolutionPath = 'Normal edit' | 'Rerun' | 'Exception request';
export type SchedulerReviewAssignmentMode = 'solver' | 'template_default' | 'locked_manual' | 'approved_exception';
export type SchedulerReviewShiftTone = 'danger' | 'warning' | 'success' | 'info';

type RawSchedulerReviewGroup = {
  id: number;
  name: string;
  slug: string;
  company: {
    id: number;
    name: string;
    slug: string;
  };
};

type RawSchedulerReviewConflictItem = {
  id: string;
  severity: SchedulerReviewConflictSeverity;
  title: string;
  summary: string;
  unit_label: string;
  root_cause: string;
  resolution_path: SchedulerReviewResolutionPath;
  shift_id: string;
  blocker_text?: string | null;
  notes?: string[] | null;
};

type RawSchedulerReviewResponse = {
  selected_group: RawSchedulerReviewGroup;
  available_groups: RawSchedulerReviewGroup[];
  review: {
    group_name: string;
    schedule_period: string;
    draft_revision_id: string;
    run_id: string;
    generated_at: string;
    result_status: SchedulerReviewStatus;
    counters: {
      unfilled_shifts: number;
      hard_conflicts: number;
      soft_warnings: number;
      locked_assignments: number;
      manual_edits: number;
    };
    preservation_mode: string;
    publish_reason: string;
    data_notice: string;
    data_source: string;
    conflict_sections: Array<{
      title: string;
      items: RawSchedulerReviewConflictItem[];
    }>;
    schedule_days: Array<{
      day_label: string;
      shifts: Array<{
        id: string;
        day_label: string;
        lane_label: string;
        time_label: string;
        assignment_label: string;
        assignment_mode: SchedulerReviewAssignmentMode;
        status_tone: SchedulerReviewShiftTone;
        issue_count: number;
        issue_ids: string[];
        summary: string;
        why_this_assignment_won: string[];
        why_others_failed: string[];
        next_steps: string[];
      }>;
    }>;
  };
  meta: {
    uses_demo_data: boolean;
    period_start: string;
    period_end: string;
  };
};

export type SchedulerReviewGroup = {
  id: number;
  name: string;
  slug: string;
  company: {
    id: number;
    name: string;
    slug: string;
  };
};

export type SchedulerReviewConflictItem = {
  id: string;
  severity: SchedulerReviewConflictSeverity;
  title: string;
  summary: string;
  unitLabel: string;
  rootCause: string;
  resolutionPath: SchedulerReviewResolutionPath;
  shiftId: string;
  blockerText?: string;
  notes?: string[];
};

export type SchedulerReviewResponse = {
  selectedGroup: SchedulerReviewGroup;
  availableGroups: SchedulerReviewGroup[];
  review: {
    groupName: string;
    schedulePeriod: string;
    draftRevisionId: string;
    runId: string;
    generatedAt: string;
    resultStatus: SchedulerReviewStatus;
    counters: {
      unfilledShifts: number;
      hardConflicts: number;
      softWarnings: number;
      lockedAssignments: number;
      manualEdits: number;
    };
    preservationMode: string;
    publishReason: string;
    dataNotice: string;
    dataSource: string;
    conflictSections: Array<{
      title: string;
      items: SchedulerReviewConflictItem[];
    }>;
    scheduleDays: Array<{
      dayLabel: string;
      shifts: Array<{
        id: string;
        dayLabel: string;
        laneLabel: string;
        timeLabel: string;
        assignmentLabel: string;
        assignmentMode: SchedulerReviewAssignmentMode;
        statusTone: SchedulerReviewShiftTone;
        issueCount: number;
        issueIds: string[];
        summary: string;
        whyThisAssignmentWon: string[];
        whyOthersFailed: string[];
        nextSteps: string[];
      }>;
    }>;
  };
  meta: {
    usesDemoData: boolean;
    periodStart: string;
    periodEnd: string;
  };
};

export type DraftVersion = {
  id: number;
  candidate_id: string;
  revision: number;
  artifact_version: string;
  run_id: string;
  period_starts_on: string;
  period_ends_on: string;
  produced_at: string;
  review_state: string;
  supersedes: { id: number; revision: number } | null;
};

export type DraftWorkflow = {
  versions: DraftVersion[];
  compare: Array<DraftVersion & { assignments: unknown[]; findings: unknown[]; metrics: Record<string, unknown> }>;
  generation_requests: Array<{ receipt_id: string; horizon_days: number; status: string; created_at: string }>;
};

const readJson = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    throw new Error(
      response.status === 403
        ? 'You do not have scheduler review access. Please contact your administrator to obtain access.'
        : `Scheduler draft review request failed (${response.status}).`
    );
  }

  return response.json() as Promise<T>;
};

const mapGroup = (group: RawSchedulerReviewGroup): SchedulerReviewGroup => ({
  id: group.id,
  name: group.name,
  slug: group.slug,
  company: group.company,
});

export const fetchSchedulerDraftReview = async (query: {
  group?: string;
  periodStart?: string;
  periodEnd?: string;
} = {}): Promise<SchedulerReviewResponse> => {
  const params = new URLSearchParams();

  if (query.group) params.set('group', query.group);
  if (query.periodStart) params.set('period_start', query.periodStart);
  if (query.periodEnd) params.set('period_end', query.periodEnd);

  const suffix = params.toString() ? `?${params.toString()}` : '';
  const payload = await readJson<RawSchedulerReviewResponse>(
    await fetch(`/api/v1/secure/scheduler-review/draft-review${suffix}`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
  );

  return {
    selectedGroup: mapGroup(payload.selected_group),
    availableGroups: payload.available_groups.map(mapGroup),
    review: {
      groupName: payload.review.group_name,
      schedulePeriod: payload.review.schedule_period,
      draftRevisionId: payload.review.draft_revision_id,
      runId: payload.review.run_id,
      generatedAt: payload.review.generated_at,
      resultStatus: payload.review.result_status,
      counters: {
        unfilledShifts: payload.review.counters.unfilled_shifts,
        hardConflicts: payload.review.counters.hard_conflicts,
        softWarnings: payload.review.counters.soft_warnings,
        lockedAssignments: payload.review.counters.locked_assignments,
        manualEdits: payload.review.counters.manual_edits,
      },
      preservationMode: payload.review.preservation_mode,
      publishReason: payload.review.publish_reason,
      dataNotice: payload.review.data_notice,
      dataSource: payload.review.data_source,
      conflictSections: payload.review.conflict_sections.map((section) => ({
        title: section.title,
        items: section.items.map((item) => ({
          id: item.id,
          severity: item.severity,
          title: item.title,
          summary: item.summary,
          unitLabel: item.unit_label,
          rootCause: item.root_cause,
          resolutionPath: item.resolution_path,
          shiftId: item.shift_id,
          blockerText: item.blocker_text ?? undefined,
          notes: item.notes ?? undefined,
        })),
      })),
      scheduleDays: payload.review.schedule_days.map((day) => ({
        dayLabel: day.day_label,
        shifts: day.shifts.map((shift) => ({
          id: shift.id,
          dayLabel: shift.day_label,
          laneLabel: shift.lane_label,
          timeLabel: shift.time_label,
          assignmentLabel: shift.assignment_label,
          assignmentMode: shift.assignment_mode,
          statusTone: shift.status_tone,
          issueCount: shift.issue_count,
          issueIds: shift.issue_ids,
          summary: shift.summary,
          whyThisAssignmentWon: shift.why_this_assignment_won,
          whyOthersFailed: shift.why_others_failed,
          nextSteps: shift.next_steps,
        })),
      })),
    },
    meta: {
      usesDemoData: payload.meta.uses_demo_data,
      periodStart: payload.meta.period_start,
      periodEnd: payload.meta.period_end,
    },
  };
};

export const fetchDraftWorkflow = async (company: string, group: string, compare: number[] = []): Promise<DraftWorkflow> => {
  const params = new URLSearchParams({ company, group });
  if (compare.length) params.set('compare', compare.join(','));
  return readJson<DraftWorkflow>(await fetch(`/api/v1/secure/scheduler-review/drafts?${params.toString()}`, {
    credentials: 'same-origin', headers: { Accept: 'application/json' },
  }));
};

const postWorkflow = async <T>(url: string, body: Record<string, unknown>): Promise<T> => readJson<T>(await fetch(url, {
  method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-CSRF-TOKEN': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '' }, body: JSON.stringify(body),
}));

export const requestDraftGeneration = (company: string, group: string, horizonStartsOn: string, horizonDays: number) =>
  postWorkflow<{ receipt: { receipt_id: string } }>('/api/v1/secure/scheduler-review/drafts/generation-requests', { company, group, horizon_starts_on: horizonStartsOn, horizon_days: horizonDays });

export const submitDraftDecision = (company: string, group: string, version: DraftVersion, action: 'accept' | 'reject', note: string) =>
  postWorkflow<{ decision: { receipt_id: string }; activation_performed: false; publication_performed: false }>(`/api/v1/secure/scheduler-review/drafts/${version.id}/decisions`, { company, group, expected_revision: version.revision, action, note });

export const submitChronosFeedback = (company: string, group: string, version: DraftVersion, classification: 'one_off' | 'candidate_rule_change', message: string) =>
  postWorkflow<{ receipt: { receipt_id: string; delivery_status: string } }>(`/api/v1/secure/scheduler-review/drafts/${version.id}/feedback`, { company, group, classification, message });
